import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import API from "../api/api";
import { BACKEND_URL } from "../api/api";
import toast from "react-hot-toast";
import AvailabilityCalendar from "../components/AvailabilityCalendar";
import CameraFileInput from "../components/CameraFileInput";
import PublicFooter from "../components/PublicFooter";
import { contactErrors } from "../utils/contactValidation";

const parsePhotos = (value) => {
  try {
    const parsed = JSON.parse(value || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export default function Home({ user, onLogout }) {
  const [activeModule, setActiveModule] = useState("home");
  const [selectedTrailerId, setSelectedTrailerId] = useState(null);
  const [activeDateField, setActiveDateField] = useState("startDate");
  const [form, setForm] = useState({
    firstName: "",
    name: "",
    address: "",
    phone: "",
    email: "",
    startDate: "",
    endDate: ""
  });
  const [files, setFiles] = useState({
    idCard: [],
    license: [],
    vehicle: []
  });
  const validationErrors = contactErrors(form.phone, form.email);

  const { data: trailers = [] } = useQuery({
    queryKey: ["trailers"],
    queryFn: async () => (await API.get("/trailers")).data
  });
  const { data: reservations = [] } = useQuery({
    queryKey: ["reservations"],
    queryFn: async () => (await API.get("/reservations")).data
  });

  const selectedTrailer = useMemo(
    () => trailers.find((trailer) => trailer.id === selectedTrailerId),
    [trailers, selectedTrailerId]
  );

  const featuredTrailers = useMemo(() => {
    const reservationCounts = reservations.reduce((counts, reservation) => {
      counts[reservation.trailerId] = (counts[reservation.trailerId] || 0) + 1;
      return counts;
    }, {});

    return [...trailers]
      .sort((a, b) => (reservationCounts[b.id] || 0) - (reservationCounts[a.id] || 0))
      .slice(0, 3);
  }, [trailers, reservations]);

  const uploadFiles = async (fileList) => {
    const paths = [];

    for (const file of fileList) {
      const formData = new FormData();
      formData.append("image", file);
      const res = await API.post("/upload", formData);
      paths.push(res.data.filePath);
    }

    return paths;
  };

  const reservationMutation = useMutation({
    mutationFn: async () => {
      if (!selectedTrailer) throw new Error("Selecteer eerst een trailer");
      if (!form.firstName || !form.name || !form.address || !form.phone || !form.email || !form.startDate || !form.endDate) {
        throw new Error("Vul alle klant- en reservatiegegevens in");
      }
      if (validationErrors.phone || validationErrors.email) {
        throw new Error(validationErrors.phone || validationErrors.email);
      }
      if (!files.idCard.length || !files.license.length || !files.vehicle.length) {
        throw new Error("Identiteitskaart, rijbewijs en identificatieverslag zijn verplicht");
      }
      const idCardDocs = await uploadFiles(files.idCard);
      const licenseDocs = await uploadFiles(files.license);
      const vehicleDocs = await uploadFiles(files.vehicle);
      const documents = JSON.stringify({ idCardDocs, licenseDocs, vehicleDocs });
      const customer = await API.post("/customers", {
        firstName: form.firstName,
        lastName: form.name,
        address: form.address,
        phone: form.phone,
        email: form.email,
        documents
      });

      return API.post("/reservations", {
        ...form,
        customerId: customer.data.id,
        trailerId: selectedTrailer.id,
        idCardDocs: JSON.stringify(idCardDocs),
        licenseDocs: JSON.stringify(licenseDocs),
        vehicleDocs: JSON.stringify(vehicleDocs)
      });
    },
    onSuccess: () => {
      toast.success("Reservatie aangevraagd");
      setForm({ firstName: "", name: "", address: "", phone: "", email: "", startDate: "", endDate: "" });
      setFiles({ idCard: [], license: [], vehicle: [] });
    },
    onError: (error) => toast.error(error.response?.data?.error || error.message || "Reservatie aanvragen mislukt")
  });

  const selectDate = (dateValue) => {
    setForm({ ...form, [activeDateField]: dateValue });
    if (activeDateField === "startDate") setActiveDateField("endDate");
  };

  return (
    <main className="public-shell">
      <aside className="public-sidebar">
        <div className="sidebar-brand">
          <p className="sidebar-brand-title">DR-Renting</p>
          <p className="sidebar-brand-subtitle">Aanbod</p>
        </div>
        <button
          className={`sidebar-button${activeModule === "trailers" ? " active" : ""}`}
          onClick={() => setActiveModule("trailers")}
        >
          Trailers
        </button>
        <Link className="sidebar-button" to="/contact">Contact</Link>
        {user && <Link className="sidebar-button" to="/dashboard">Dashboard</Link>}
        <div className="public-sidebar-bottom">
          {user ? (
            <button className="sidebar-button danger" onClick={onLogout}>Afmelden</button>
          ) : (
            <Link className="sidebar-button" to="/login">Medewerker login</Link>
          )}
        </div>
      </aside>

      <section className="public-page">
      <header className="public-header">
        <div>
          <h1>DR-Renting</h1>
          <p>Bekijk ons aanbod, controleer beschikbaarheid en reserveer online.</p>
        </div>
      </header>

      {activeModule !== "trailers" ? (
        <div className="public-intro panel panel-pad">
          <h2>Welkom</h2>
          <p>Kies links een categorie om het beschikbare aanbod te bekijken.</p>
          <div className="featured-trailers">
            <div className="section-heading">
              <h3>Meest gekozen trailers</h3>
              <p>Een snelle selectie uit het populairste aanbod.</p>
            </div>
            <div className="featured-grid">
              {featuredTrailers.map((trailer) => {
                const thumbnail = parsePhotos(trailer.photos)[0];
                return (
                  <Link key={trailer.id} className="featured-card" to={`/aanbod/trailers/${trailer.id}`} onClick={() => setSelectedTrailerId(trailer.id)}>
                    <img src={thumbnail ? `${BACKEND_URL}${thumbnail}` : "/logo192.png"} alt={trailer.name} />
                    <span className="featured-name">{trailer.name}</span>
                    <span className="featured-meta">
                      {trailer.length || "-"} m x {trailer.width || "-"} m - MTM {trailer.mtm} kg
                    </span>
                    <span className="badge info">Rijbewijs {trailer.licenseType || "-"}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
      <section className="public-grid">
        <div className="list">
          {trailers.map((trailer) => {
            const thumbnail = parsePhotos(trailer.photos)[0];
            return (
              <Link key={trailer.id} className={`list-row reservation-row${selectedTrailerId === trailer.id ? " selected" : ""}`} to={`/aanbod/trailers/${trailer.id}`} onClick={() => setSelectedTrailerId(trailer.id)}>
                <div className="trailer-offer">
                  <img className="offer-thumb" src={thumbnail ? `${BACKEND_URL}${thumbnail}` : "/logo192.png"} alt={trailer.name} />
                  <div>
                    <div className="row-title">{trailer.name}</div>
                    <div className="row-meta">
                      Lengte {trailer.length || "-"} m - Breedte {trailer.width || "-"} m - MTM {trailer.mtm} kg - Laden {trailer.maxLoadWeight || "-"} kg - Rijbewijs {trailer.licenseType || "-"}
                    </div>
                    <div className="status-line">
                      <span className="badge info">EUR {trailer.pricePerDay} per dag</span>
                      <span className="badge warning">Waarborg EUR {trailer.depositAmount || 0}</span>
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        <aside className="panel panel-pad detail-panel">
          {!selectedTrailer ? (
            <div className="empty-state">Selecteer een trailer om details en beschikbaarheid te bekijken.</div>
          ) : (
            <>
              <div className="detail-section">
                <h2 className="page-title" style={{ fontSize: 24 }}>{selectedTrailer.name}</h2>
                <p className="page-subtitle">{selectedTrailer.extraInfo || "Bekijk de beschikbaarheid en vul daarna je gegevens in."}</p>
              </div>
              <div className="detail-section">
                <AvailabilityCalendar
                  trailerId={selectedTrailer.id}
                  reservations={reservations}
                  selectedStartDate={form.startDate}
                  selectedEndDate={form.endDate}
                  onSelectDate={selectDate}
                />
              </div>
              <div className="detail-section">
                <h3 className="detail-title">Reservatiegegevens</h3>
                <div className="form-grid">
                  <label className="field"><span className="field-label">Voornaam</span><input className="input" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} /></label>
                  <label className="field"><span className="field-label">Naam</span><input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
                  <label className="field"><span className="field-label">Telefoonnummer</span><input className="input" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />{validationErrors.phone && <span className="field-error">{validationErrors.phone}</span>}</label>
                  <label className="field"><span className="field-label">E-mail</span><input className="input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />{validationErrors.email && <span className="field-error">{validationErrors.email}</span>}</label>
                  <label className="field"><span className="field-label">Startdatum</span><input className="input" type="date" value={form.startDate} onFocus={() => setActiveDateField("startDate")} onChange={(e) => setForm({ ...form, startDate: e.target.value })} /></label>
                  <label className="field"><span className="field-label">Einddatum</span><input className="input" type="date" value={form.endDate} onFocus={() => setActiveDateField("endDate")} onChange={(e) => setForm({ ...form, endDate: e.target.value })} /></label>
                </div>
                <label className="field" style={{ marginTop: 12 }}><span className="field-label">Adres</span><input className="input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></label>
              </div>
              <div className="detail-section">
                <h3 className="detail-title">Documenten uploaden</h3>
                <div className="document-upload-grid">
                  <CameraFileInput id="home-id-card" label="Identiteitskaart voor- en achterkant" value={files.idCard} multiple onChange={(nextFiles) => setFiles({ ...files, idCard: nextFiles })} />
                  <CameraFileInput id="home-license" label="Rijbewijs voor- en achterkant" value={files.license} multiple onChange={(nextFiles) => setFiles({ ...files, license: nextFiles })} />
                  <CameraFileInput id="home-vehicle" label="Identificatieverslag wagen" value={files.vehicle} multiple onChange={(nextFiles) => setFiles({ ...files, vehicle: nextFiles })} />
                </div>
              </div>
              <button className="button" onClick={() => reservationMutation.mutate()}>Reservatie aanvragen</button>
            </>
          )}
        </aside>
      </section>
      )}
      <PublicFooter />
      </section>
    </main>
  );
}
